// Mock payload shaped exactly like the real
// assets.OPERA.cn/service/weather/overview -> responses[0].weather[0]
// so the app can be switched to the live endpoint with zero code change.

const isoDay = (offsetDays = 0, hour = 0) => {
  const d = new Date()
  d.setMinutes(0, 0, 0)
  d.setHours(0 + hour)
  d.setDate(d.getDate() + offsetDays)
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00:00+08:00`
}

const WEEKDAYS = ['今天', '昨天', '星期二', '星期三', '星期四', '星期五', '星期六', '星期日']
const weekdayOf = (i) => {
  const d = new Date()
  d.setDate(d.getDate() + i)
  return ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'][d.getDay()]
}

const ICON = {
  clearDay: 1, mostlyClearDay: 2, partlyCloudyDay: 4, cloudy: 6,
  rain: 12, showers: 12, thunderstorm: 15, rainAndSnow: 20, snow: 21,
  clearNight: 29, mostlyClearNight: 30, partlyCloudyNight: 33
}

const symbols = ['d000', 'd100', 'd200', 'd240', 'd300', 'd400', 'n100']

const mkHalf = (cap, icon, symbol, precip, sky, dir, spd) => ({
  cap, pvdrCap: cap,
  pvdrWindDir: String(dir), pvdrWindSpd: String(spd),
  icon, symbol, pvdrIcon: String(icon),
  urlIcon: '//img-s-OPERA-com.akamaized.net/tenant/amp/entityid/AAehR3S.img',
  precip, sky,
  windDir: dir, windSpd: spd,
  summary: `${cap}。`,
  summaries: [cap, `${cap}，风力 ${spd} 级。`]
})

const mkDay = (i) => {
  // pseudo-deterministic generator so the UI is stable across reloads (avoids CLS)
  const seed = (i * 37) % 11
  const hi = 34 - i % 4 + (seed % 3)
  const lo = 21 - i % 3 - (seed % 2)
  const isRainy = i >= 6
  const dayCap = isRainy ? '阵雨' : seed % 4 === 0 ? '晴朗' : '大部晴朗'
  const nightCap = isRainy ? '局部阵雨' : '以晴为主'
  const precip = isRainy ? 40 + seed : seed > 7 ? 1 : 0

  return {
    hourly: Array.from({ length: 12 }, (_, h) => {
      const hour = h * 2
      const temp = Math.round(lo + ((hi - lo) * Math.sin(((hour + 2) / 24) * Math.PI)) * 0.95)
      const rainyHour = isRainy && hour >= 12
      const capH = rainyHour ? '阵雨' : hour < 6 || hour > 19 ? nightCap : dayCap
      const iconH = rainyHour ? ICON.rain : hour < 6 || hour > 19 ? ICON.clearNight : ICON.mostlyClearDay
      return {
        valid: isoDay(i, hour),
        hour: `${String(hour).padStart(2, '0')}:00`,
        cap: capH,
        pvdrCap: capH,
        pvdrWindDir: '南风', pvdrWindSpd: '3级',
        icon: iconH,
        symbol: rainyHour ? 'd400' : hour < 6 || hour > 19 ? 'n100' : 'd100',
        pvdrIcon: String(iconH),
        wx: '', sky: rainyHour ? 'OVC' : 'SCT',
        temp,
        feels: temp + 2,
        precip: rainyHour ? 40 + ((h % 5) * 10) : 0,
        rh: 30 + ((seed + h) % 40),
        windSpd: 4 + ((seed + h) % 8),
        windDir: 180 + ((seed + h) % 90),
        windGust: 10 + ((seed + h) % 14),
        uv: Math.max(0, Math.round(8 * Math.sin((Math.max(0, hour - 6) / 12) * Math.PI)) - (i % 3)),
        vis: 10 + (seed % 20),
        baro: 1012 + ((seed * 3 + h) % 9),
        dewPt: temp - 9,
        cloudCover: (seed * 7 + h * 5) % 100,
        rainAmount: rainyHour ? Math.round((1 + (h % 4)) * 10) / 10 : 0,
        snowAmount: 0,
        raAccu: 0, saAccu: 0,
        // 逐小时波动的 AQI（早高峰/夜间偏高），供详情页「空气指数」曲线使用
        aqi: 32 + ((seed * 7 + h * 9 + (hour >= 7 && hour <= 10 ? 22 : hour >= 20 ? 14 : 0)) % 95)
      }
    }),
    daily: {
      valid: isoDay(i),
      day: mkHalf(dayCap, ICON.mostlyClearDay, symbols[seed % symbols.length], precip, isRainy ? 'OVC' : 'SCT', 181, 6),
      night: mkHalf(nightCap, ICON.clearNight, 'n100', isRainy ? 20 : 1, 'SCT', 348, 5),
      pvdrCap: isRainy ? '阵雨' : '局部晴朗',
      pvdrWindDir: '南风', pvdrWindSpd: '微风',
      icon: isRainy ? ICON.rain : ICON.mostlyClearDay,
      symbol: isRainy ? 'd400' : 'd100',
      pvdrIcon: String(isRainy ? ICON.rain : ICON.mostlyClearDay),
      iconUrl: '//img-s-OPERA-com.akamaized.net/tenant/amp/entityid/AAehR3S.img',
      precip,
      windMax: 8 + (seed % 6),
      windMaxDir: 181,
      // 日摘要使用 km/h 口径：平均风速、夜间最大阵风（与 live 数据层字段保持一致）
      windAvgKmh: 6 + (seed % 5) * 2,
      gustNightKmh: 10 + (seed % 14),
      windTh: 22,
      rhHi: 82, rhLo: 25, rh: 54,
      tempHi: hi, tempLo: lo,
      tempHiD: Math.round(hi * 9 / 5 + 32), tempLoD: Math.round(lo * 9 / 5 + 32),
      uv: 7 - (i % 3), uvDesc: ['低', '中', '高', '极高'][(7 - (i % 3)) > 7 ? 3 : (7 - (i % 3)) > 5 ? 2 : (7 - (i % 3)) > 2 ? 1 : 0],
      created: isoDay(0, 15),
      rainAmount: isRainy ? 3.2 : 0,
      snowAmount: 0,
      raToMN: 0, saToMN: 0,
      feelsHi: hi + 3, feelsLo: lo + 2,
      vis: 10 + (seed % 20),
      dewPt: lo - 6,
      baro: 1014 + (seed % 4),
      aqi: 70 + (seed % 20)
    },
    almanac: (() => {
      const f = (n) => String(n).padStart(2, '0')
      const fmt = (d) => `${d.getFullYear()}-${f(d.getMonth()+1)}-${f(d.getDate())}T${f(d.getHours())}:${f(d.getMinutes())}:00+08:00`
      const a = new Date(); a.setHours(5, 45, 0, 0); a.setDate(a.getDate() + i)
      const b = new Date(); b.setHours(18, 42, 0, 0); b.setDate(b.getDate() + i)
      return {
        valid: isoDay(i),
        sunrise: fmt(a),
        sunset: fmt(b),
        moonPhase: ['下弦月', '亏凸月', '残月', '新月', '娥眉月'][i % 5],
        moonPhaseCode: ['LQtr', 'WxGb', 'WnCr', 'NewM', 'WxCc'][i % 5]
      }
    })()
  }
}

/**
 * 生活/活动指数 — mirrors the real LifeActivityResp.lifeDailyIndices item:
 * { type, subType, value, category, summary, taskbarSummary, hourlyIndices }
 * subType: 24=户外活动 1=穿衣 10=雨伞 2=紫外线指数
 */
const ratingOf = (subType, value) => {
  if (subType === 24) {
    if (value >= 8) return '非常适宜'
    if (value >= 6) return '适宜'
    if (value >= 4) return '一般'
    return '不太适宜'
  }
  if (subType === 2) {
    if (value <= 2) return '低'
    if (value <= 5) return '中等'
    if (value <= 7) return '高'
    if (value <= 10) return '很高'
    return '极端'
  }
  if (subType === 1) {
    if (value >= 24) return '透气的衣服'
    if (value >= 18) return '短袖'
    if (value >= 12) return '长袖'
    return '保暖外套'
  }
  if (subType === 10) return value >= 200 ? '无需带伞' : value >= 100 ? '建议带伞' : '必须带伞'
  return '—'
}

const mkLifeIndices = (i, isRainy) => {
  const outdoor = Math.max(1, 9 - (i % 5) - (isRainy ? 4 : 0))
  const uv = 3 + ((i * 2) % 4)
  const clothing = 25 - (i % 6)
  const umbrella = isRainy ? 60 : 300
  const mk = (type, subType, value, category, summary) => ({
    type, subType, value, category,
    validTime: 0, rankId: 0,
    summary, shortSummary: '', taskbarSummary: ratingOf(subType, value),
    hourlyIndices: [], validTimeRangeInHour: [68]
  })
  return [
    mk(3, 24, outdoor, 1, outdoor >= 6
      ? '5:00 PM 到 11:59 PM 的天气非常适合户外活动。做好准备，享受户外时光!'
      : '天气条件一般，如需户外活动请注意防护。'),
    mk(1, 1, clothing, 2, clothing >= 24 ? '由于过热，建议下午穿透气的衣服。' : '天气转凉，建议适当添加衣物。'),
    mk(2, 10, umbrella, 1, umbrella >= 200 ? '下午无需带伞。' : '今天有降水，出门请带伞。'),
    mk(1, 2, uv, 2, '今日紫外线指数' + ratingOf(2, uv) + '。建议使用防晒霜或其他紫外线防护物品。尽量避免在上午 11 点到下午 4 点期间暴晒。')
  ]
}

export const mockOverview = {
  responses: [
    {
      source: {
        // No hardcoded default city (old mock shipped 北京市/海淀区, id 101010200).
        // Identity stays neutral; coordinates are overwritten with the caller's real
        // front-end lat/lon at fetch time (see weatherApi.fetchOverview).
        id: '',
        coordinates: { lat: 0, lon: 0 },
        location: {
          Name: '',
          StateCode: '',
          CountryName: '',
          CountryCode: '',
          TimezoneName: 'Asia/Shanghai',
          TimezoneOffset: '08:00:00'
        },
        utcOffset: '08:00:00',
        countryCode: 'CN'
      },
      weather: [
        {
          alerts: [],
          current: {
            baro: 1014.0, cap: '晴朗', capAbbr: '晴朗', daytime: 'd',
            dewPt: 13.0, feels: 37.0, rh: 31.0,
            icon: 1, symbol: 'd000', pvdrIcon: '1',
            urlIcon: '//img-s-OPERA-com.akamaized.net/tenant/amp/entityid/AAehR3S.img',
            wx: '', sky: 'CLR',
            temp: 33.0, tempD: 91.4, tempDesc: 9,
            utci: 37.0, uv: 5.0, uvDesc: '中',
            vis: 30.0, windDir: 197, windSpd: 6.0, windTh: 22.0, windGust: 16.0,
            created: isoDay(0, 15), pvdrCap: '晴朗',
            pvdrWindDir: '南风', pvdrWindSpd: '2级',
            aqi: 70.0, aqiSeverity: '空气质量中等', aqLevel: 20,
            primaryPollutant: 'NO₂ 0.0 ppb',
            aqiValidTime: isoDay(0, 15),
            richCaps: [], cloudCover: 4.0
          },
          forecast: { days: Array.from({ length: 10 }, (_, i) => mkDay(i)) },
          nowcasting: {
            precipitation: Array.from({ length: 52 }, () => 0),
            precipitationRate: Array.from({ length: 52 }, () => 0),
            precipitationAccumulation: Array.from({ length: 52 }, () => 0),
            previousPrecipitation: [], symbols: [], previousSymbols: [],
            templateType: 'NoRain',
            minutesToTransit: 0,
            summary: '至少 2 小时内没有降水。',
            shortSummary: '至少 2 小时内没有降水',
            taskbarSummary: '120分钟内无降水',
            horrizonCount: 52,
            minutesBetweenHorrizons: 4,
            enableRainSignal: false,
            raintype: 'nr',
            timestamp: isoDay(0, 15),
            weathertype: 255,
            nowcastingDistance: 0,
            nearbyPrecipitationType: 255,
            taskbarSummaryL1: '', taskbarSummaryL2: ''
          },
          mapsmetadata: {
            NowcastVectorMap: {
              uris: {
                '202609040724':
                  'https://assets.OPERA.com/weathermapdata/1/nowcastmap/202609040724/Sbn/{z}/{x}_{y}_6.blc'
              }
            },
            TileImageMetadata: {
              uris: {
                '202609031200':
                  'https://assets.OPERA.com/weathermapdata/1/{scenario}/090312/{x}_{y}_{z}_{time}.{fmt}'
              }
            }
          },
          contentdata: [
            {
              id: 'wxnwtsstrs', cid: 0, ranking: 950,
              contenttype: 'TeaserRainStatistics', isSpotlight: false,
              content: [
                {
                  title: '未来几天多雨',
                  cap: '预计自星期一起，会有2天降雨。',
                  capIcon: 'rainstart0.svg'
                }
              ]
            }
          ],
          LifeActivityResp: {
            days: Array.from({ length: 11 }, (_, i) => {
              const isRainyDay = i >= 6
              return {
                valid: isoDay(i),
                tempHigh: 34 - i % 4,
                tempLow: 21 - i % 3,
                dailySymbol: isRainyDay ? 'd400' : 'd100',
                lifeDailyIndices: mkLifeIndices(i, isRainyDay),
                lifeTips: [
                  { id: 't1', title: '压力袜', content: '户外活动时穿压力袜以促进血液循环，并减少肌肉震动。' },
                  { id: 't2', title: '雨后运动', content: '在雨后运动，享受更清新的空气和更低的湿度——随后通常天空晴朗。' },
                  { id: 't3', title: '防止紧绷感', content: '重点对运动时的主要肌肉(例如，腿筋和小腿)进行轻柔拉伸，以降低抽筋或受伤的风险。' }
                ]
              }
            })
          },
          provider: {
            name: '中国天气网',
            url: '',
            gridEnabled: 1
          },
          aqiProvider: { name: 'wxforecast' },
          wazes: [],
          insights: {
            insights: [
              {
                category: 'Temperature',
                type: 'Online-TemperaturePastDay',
                teaserText: '', headlineText: '',
                fullText: '日间最高气温为 33°。',
                parameters: { dailyHigh: 33.0, nightlyLow: 19.0 },
                metadata: {
                  unit_nightlyLow: '°', unit_dailyHigh: '°',
                  noSpaceBeforeUnit: 'true',
                  validDateTime: '2026/09/03',
                  NightSummary: '夜间最低气温为 19°。'
                },
                timeSeries: { temperature: {} },
                unitText: '°C', icon: 'recordlow.svg', trend: 'Steady'
              }
            ]
          }
        }
      ]
    }
  ],
  units: {
    system: 'Metric',
    pressure: 'hPa',
    temperature: '°C',
    speed: 'km/h',
    height: 'mm',
    distance: 'km',
    time: 's'
  },
  userProfile: { used: false },
  copyright:
    'Copyright © 2026 Microsoft and its suppliers. All rights reserved. This API cannot be accessed and the content and any results may not be used, copied or reproduced without express written permission.'
}

// Preset city list removed on purpose: the app must not ship hardcoded default
// cities (北京市/上海市/深圳市/…). The city picker now starts empty and is filled
// only by real front-end geolocation or the user's searches.
export const mockPlaces = []

export { WEEKDAYS, weekdayOf }
